import { useMemo, useState, useRef } from "react";
import { DatesProvider } from "dates/DatesContext";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import Loading from "components/core/blocks/Loading";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import { Container, Row, Col } from "react-bootstrap";
import { useModuleEnabled } from "redux/selectors/modules";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import { useAppSelector } from "../../redux/hooks";
import CouponReport from "views/merchandise/components/CouponReport";
import { formatCurrency, formatPercent, formatNumber } from "utils/formatNumber";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import Accordion from "components/core/basic/Accordion";
import ReactTable from "react-table-6";
import ReactTablePagination from "components/custom/growth/ProductsCVTablePagination";
import useColumnSelector from "hooks/useColumnSelector";
import TrComponent from "components/custom/merchandise/TrComponent";
import { useParams } from "react-router-dom";
import { ProductCell } from "views/merchandise/cells/ProductCell";
import moment from "moment";
import { CurrentVsPreviousGroupedColumnChart } from "modules/pricingv2/charts/CurrentVsPreenrollmentChart";
import { token } from "../../../styled-system/tokens";

// TODO factor this out into a utils file
const DASHBOARD_TYPES = {
  PRODUCT: "product",
  CATEGORY: "category",
  ROOT: "root",
};

const getDashboardTo = ({ resourceId, type }) => {
  let to;
  switch (type) {
    case DASHBOARD_TYPES.CATEGORY:
      to = `/user/merchandising/category/${resourceId}`;
      break;
    case DASHBOARD_TYPES.PRODUCT:
      to = `/user/merchandising/product/${resourceId}`;
      break;
    default:
      to = `/user/merchandising/dashboard`;
  }
  return to;
};

const Metric = ({ title, value, previous }) => {
  return (
    <Col style={{ flex: "0 0 auto", width: "16.666667%", maxWidth: "16.666667%" }} className="col-xl-2 col-lg-3 col-6">
      <div className="p-4 mb-3 card">
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div className="text-end" style={{ marginRight: "10px" }}>
            <h6 className="fs-standard" style={{ height: "34px", fontWeight: 800 }}>
              {title}
            </h6>
            <h3 style={{ fontWeight: 600, fontSize: "2rem", marginBottom: "0.2rem", marginTop: "0.2rem" }}>{value}</h3>

            <div style={{ minHeight: "20px" }}>
              <small>
                {previous && <span style={{ color: "rgb(44, 44, 44)", fontWeight: "bold" }}>{previous}</span>}
                {!previous && <span style={{ color: "rgb(44, 44, 44)", fontWeight: "bold" }}>&nbsp;</span>}
              </small>
            </div>
          </div>
          <div
            style={{
              transition: "0.3s ease-in-out",
              marginLeft: "1rem",
              backgroundColor: "rgb(98, 63, 127)",
              borderColor: "rgb(98, 63, 127)",
              borderWidth: "1px",
              width: "6px",
              borderRadius: "4px",
              display: "block",
              boxShadow: "rgb(98, 63, 127) 0px 0px 14px",
            }}
          ></div>
        </div>
      </div>
    </Col>
  );
};

const incrementMetrics = (couponMetrics, coupon) => {
  couponMetrics.redemptions += coupon.metrics.redemptions ?? 0;
  couponMetrics.clips += coupon.metrics.clips ?? 0;
  couponMetrics.budget_spent += coupon.metrics.budget_spent ?? 0;
  couponMetrics.sales += coupon.metrics.sales ?? 0;
  couponMetrics.total_discount += coupon.metrics.total_discount ?? 0;
  couponMetrics.budget += coupon.metrics.budget ?? 0;

  couponMetrics.redemption_rate = couponMetrics.clips > 0 ? couponMetrics.redemptions / couponMetrics.clips : 0;
  couponMetrics.sales_per_redemption =
    couponMetrics.redemptions > 0 ? couponMetrics.sales / couponMetrics.redemptions : 0;
  couponMetrics.discount_percent_of_sales =
    couponMetrics.sales > 0 ? couponMetrics.budget_spent / couponMetrics.sales : 0;
  couponMetrics.cost_per_redemption =
    couponMetrics.redemptions > 0 ? couponMetrics.budget_spent / couponMetrics.redemptions : 0;
  couponMetrics.budget_percentage_used =
    couponMetrics.budget > 0 ? couponMetrics.budget_spent / couponMetrics.budget : 0;

  return couponMetrics;
};

const defaultMetrics = {
  clips: 0,
  redemptions: 0,
  budget_spent: 0,
  budget: 0,
  sales: 0,
  redemption_rate: 0,
  total_discount: 0,
  sales_per_redemption: 0,
  discount_percent_of_sales: 0,
  cost_per_redemption: 0,
  budget_percentage_used: 0,
};

const InsightList = ({ insights, discountType, marketPlace }) => {
  const [colToggle] = useState();

  // Combine insight.values and insight.keys into an array of objects
  const insightValues = Object.values(insights);
  const insightKeys = Object.keys(insights);
  const insightData = insightValues.map((value, index) => {
    return {
      name: insightKeys[index],
      ...value,
    };
  });

  const tableRef = useRef();
  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const columns = [
      {
        id: "Discount Type",
        label: "Discount Type",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Discount Type
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        width: 150,
        accessor: "name",
        Cell: (props) => {
          return (
            <div>
              {discountType === "AMOUNT_OFF_LIST_PRICE" && (
                <div className="fw-bold">{formatCurrency(parseInt(props.value), marketPlace)}</div>
              )}
              {discountType === "PERCENT_OFF_LIST_PRICE" && (
                <div className="fw-bold">{formatPercent(parseFloat(props.value) / 100, 0)}</div>
              )}
            </div>
          );
        },
      },
      {
        id: "Budget",
        label: "Budget",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Budget
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "budget",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "BudgetSpent",
        label: "BudgetSpent",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Budget Spent
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "budget_spent",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "Sales",
        label: "Sales",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Sales
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "sales",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "Clips",
        label: "Clips",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Clips
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "clips",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "Redemptions",
        label: "Redemptions",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Redemptions
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "redemptions",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "redemption_rate",
        label: "redemption_rate",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Redemption Rate
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "redemption_rate",
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value), 1)}</div>;
        },
      },
      {
        id: "sales_per_redemption",
        label: "sales_per_redemption",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Sales per Redemption
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "sales_per_redemption",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "discount_percent_of_sales",
        label: "discount_percent_of_sales",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            ACOS
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "discount_percent_of_sales",
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value), 2)}</div>;
        },
      },
      {
        id: "budget_efficiency",
        label: "budget_efficiency",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Cost per Redemption
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "cost_per_redemption",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
    ];
    return columns;
  }, [colToggle]);

  return (
    <ReactTable
      ref={tableRef}
      data={insightData}
      className="coupon-insight-table"
      PaginationComponent={(props) => {
        return (
          <ReactTablePagination
            {...props}
            table={"coupon-insight-table"}
            showFilters={false}
            style={{ marginBottom: "2rem" }}
            carouselLayout={true}
            hideFilters={true}
            columnSelectorProps={columnSelectorProps}
            hideSearch={true}
          />
        );
      }}
      TrComponent={TrComponent}
      defaultPageSize={20}
      NoDataComponent={() => null}
      minRows={0}
      showPaginationTop
      showPaginationBottom={false}
      filterable={false}
      columns={columns}
    />
  );
};

const CouponDetails = ({ couponId }) => {
  const [colToggle] = useState();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel).currentChannel;
  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  // Pulls dates from provided context
  const { start, end, preStart, preEnd, group } = useDates();

  // Data fetching
  const filters = {
    market_place: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    channel: channel ?? "amazon",
    report_id: couponId,
  };

  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["couponDetails", marketPlace, group, start, end, preStart, preEnd, couponId],
    "/merchandise/coupons_asin_metrics",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const metrics = useMemo(() => {
    if (!couponStats) return {};

    const couponMetrics = couponStats.results[0];

    return couponMetrics;
  }, [couponStats]);

  const productData = useMemo(() => {
    if (!couponStats || !couponStats?.results || couponStats?.results.length === 0) return [];

    const stats = couponStats.results[0];
    const productDetails = stats.product_details;
    const productMetrics = stats.product_ids;

    const productData = {};
    for (const product of productMetrics) {
      productData[product.product_id] = product;
    }
    for (const product of productDetails) {
      if (product.id in productData) {
        productData[product.id] = {
          ...productData[product.id],
          ...product,
        };
      }
    }

    const data = Object.values(productData);
    // order by total_units_ordered desc
    data.sort((a, b) => b.total_quantity_ordered - a.total_quantity_ordered);

    return data;
  }, [couponStats]);

  const productReportFilters = useMemo(() => {
    if (!metrics) return null;

    // find all unique asins from metrics.product_details
    const asins = [...new Set(metrics?.product_details?.map((product) => product.asin))];
    const startDate = moment(metrics?.coupon_report?.start_time);
    let endDate = moment(metrics?.coupon_report?.end_time);

    if (endDate > moment()) {
      endDate = moment();
    }
    const diffDays = moment(endDate).diff(moment(startDate), "days");
    const preStart = moment(startDate).subtract(diffDays, "days");
    const preEnd = moment(startDate).subtract(1, "days");

    return {
      market_place: marketPlace.marketPlace,
      date_group: group,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
      pre_start_date: preStart.format("YYYY-MM-DD"),
      pre_end_date: preEnd.format("YYYY-MM-DD"),
      channel: channel ?? "amazon",
      asins: asins.join(","),
    };
  }, [metrics, marketPlace, group, channel]);

  const { data: productStats, isLoading: isProductStatsLoading } = useFetch(
    ["productDetails", marketPlace, group, start, end, preStart, preEnd, couponId],
    "/api/data_report/product_report",
    productReportFilters,
    {
      enabled: !!productReportFilters,
      select: (data) => data?.data,
    },
  );

  const breadCrumbItems = [
    {
      name: "Merchandising Dashboard",
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),

      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: "Dashboards",
    },
    {
      name: "Coupon Report",
      href: "/user/merchandising/coupons",
    },
    {
      name: metrics?.coupon_report?.name,
      href: `/user/merchandising/coupons/${couponId}`,
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  const tableRef = useRef();
  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const columns = [
      {
        id: "Product",
        label: "Product",
        Header: () => (
          <div className="text-left" style={{ width: "100%" }}>
            Product
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        width: 500,
        accessor: "product_title",
        Cell: (props) => {
          return (
            <ProductCell
              original={{
                ...props.original,
                id: props.original.product_id,
                cellContext: "couponProuctDetails",
              }}
              value={props.original.product_title}
              row={props.original}
              showAsin={true}
              showInventoryBadge={true}
            />
          );
        },
      },
      {
        id: "Sales",
        label: "Sales",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Promo Sales
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "total_difference",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "Discounts",
        label: "Discounts",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Discounts
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "total_promotion_discount",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "Units",
        label: "Units",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            Units
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "total_quantity_ordered",
        Cell: (props) => {
          return <div>{parseInt(props.value)}</div>;
        },
      },
      {
        id: "ACOS",
        label: "ACOS",
        Header: () => (
          <div className="text-center" style={{ width: "100%" }}>
            ACOS
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "total_promotion_discount",
        Cell: (props) => {
          return (
            <div>{formatPercent(parseFloat(props.value) / parseFloat(props.original.total_order_item_value), 2)}</div>
          );
        },
      },
    ];
    return columns;
  }, [colToggle]);

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col className="mb-4" xs={12}>
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>

      {(isCouponStatsLoading || isProductStatsLoading) && (
        <Row>
          <Col className="mb-4 px-3" xs={12}>
            <Loading fullPage={false} height={"300px"} />
          </Col>
        </Row>
      )}

      {!isCouponStatsLoading && !isProductStatsLoading && (
        <>
          <Row
            style={{
              display: "flex",
              flexWrap: "nowrap",
              overflow: "auto",
              paddingTop: "1rem",
              paddingBottom: "0.25rem",
              marginBottom: "0.5rem",
            }}
          >
            <Metric
              title="Redemptions / Clips"
              value={
                <>
                  {metrics?.redemptions} / {metrics?.clips}
                </>
              }
              previous={<div>Redemption Rate: {formatPercent(metrics?.redemption_rate, 1)}</div>}
            />
            <Metric
              title="Budget Spent"
              value={formatCurrency(metrics?.budget_spent, marketPlace)}
              previous={<div>Budget: {formatCurrency(metrics?.budget, marketPlace)}</div>}
            />
            <Metric
              title="Sales"
              value={formatCurrency(metrics?.sales, marketPlace)}
              previous={`Discounts ${formatCurrency(metrics?.total_discount, marketPlace)}`}
            />
            <Metric title="ACOS" value={formatPercent(metrics?.discount_percent_of_sales, 1)} />
            <Metric title="Sales per Redemption" value={formatCurrency(metrics?.sales_per_redemption, marketPlace)} />
            <Metric title="Cost per Redemption" value={formatCurrency(metrics?.cost_per_redemption, marketPlace)} />
          </Row>

          <Row>
            <Col className="px-5" xs={12}>
              <h5>
                {metrics.coupon_report.name}
                <div>
                  <small>
                    {metrics.coupon_report.website_message}

                    <span className="ps-2 fw-bold">
                      {moment(metrics.coupon_report.start_time).format("MMM Do YYYY")} -{" "}
                      {moment(metrics.coupon_report.end_time).format("MMM Do YYYY")}
                    </span>
                  </small>
                </div>
              </h5>
            </Col>
          </Row>

          <Row>
            <Col className="mb-4 px-5" xs={12}>
              <Accordion title={`Insights`} initialExpanded={true}>
                <Row>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; CR</span>
                        {formatPercent(productStats.total.conv_rate - productStats.total.conv_rate_prev, 2, true)}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: `CR`,
                            current: productStats.total.conv_rate ? productStats.total.conv_rate : 0,
                            previous: productStats.total.conv_rate_prev ? productStats.total.conv_rate_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        isPercentage
                        currentLabel="Coupon"
                      />
                    </div>
                  </Col>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; GCD</span>
                        {formatCurrency(
                          productStats.total.profit - productStats.total.profit_prev,
                          marketPlace,
                          false,
                          true,
                          false,
                        )}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: `GCD`,
                            current: productStats.total.profit ? productStats.total.profit : 0,
                            previous: productStats.total.profit_prev ? productStats.total.profit_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        isCurrency
                        currentLabel="Coupon"
                      />
                    </div>
                  </Col>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; Sales</span>
                        {formatCurrency(
                          productStats.total.total_sales - productStats.total.total_sales_prev,
                          marketPlace,
                          false,
                          true,
                          false,
                        )}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: `Sales`,
                            current: productStats.total.total_sales ? productStats.total.total_sales : 0,
                            previous: productStats.total.total_sales_prev ? productStats.total.total_sales_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        isCurrency
                        currentLabel="Coupon"
                      />
                    </div>
                  </Col>
                  <Col className="fs-5">
                    <div>
                      <div className="fs-3 text-center pb-1">
                        <span className="fw-bold pe-3">&nbsp; Volume</span>
                        {formatNumber(productStats.total.total_units - productStats.total.total_units_prev, {
                          addPlus: true,
                        })}
                      </div>
                      <CurrentVsPreviousGroupedColumnChart
                        data={[
                          {
                            title: `Sales`,
                            current: productStats.total.total_units ? productStats.total.total_units : 0,
                            previous: productStats.total.total_units_prev ? productStats.total.total_units_prev : 0,
                            color: token("colors.trellisPalette.3"),
                          },
                        ].filter(Boolean)}
                        precision={0}
                        currentLabel="Coupon"
                      />
                    </div>
                  </Col>
                </Row>
              </Accordion>
            </Col>
          </Row>

          <Row>
            <Col className="mb-4 px-5" xs={12}>
              <Accordion title={`Products`} initialExpanded={false}>
                <div
                  style={{
                    padding: "2rem",
                    border: "1px solid rgb(238, 238, 238)",
                    borderRadius: "6px",
                    backgroundColor: "rgb(248, 248, 248)",
                    marginTop: "10px",
                  }}
                  className="fs-5"
                >
                  <div className="pb-2 fw-bold">ACOS: Coupon Cost of Sales</div>
                  <div>
                    The ACOS might differ slightly from coupon value. ACOS is determined by dividing the total promotion
                    discount by the total value of ordered items. Occasionally, buyers purchase multiple items, but the
                    promotion discount applies to just one item in the order, which can affect the ACOS calculation.
                  </div>
                </div>
                <ReactTable
                  ref={tableRef}
                  data={productData}
                  className="product-insight-table"
                  PaginationComponent={(props) => {
                    return (
                      <ReactTablePagination
                        {...props}
                        table={"product-insight-table"}
                        showFilters={false}
                        style={{ marginBottom: "2rem" }}
                        carouselLayout={true}
                        hideFilters={true}
                        columnSelectorProps={columnSelectorProps}
                        hideSearch={true}
                      />
                    );
                  }}
                  TrComponent={TrComponent}
                  defaultPageSize={20}
                  NoDataComponent={() => null}
                  minRows={0}
                  showPaginationTop
                  showPaginationBottom={false}
                  filterable={false}
                  columns={columns}
                />
              </Accordion>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

const CouponDashboard = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel).currentChannel;
  const [discountType, setDiscountType] = useState("all");

  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  // Pulls dates from provided context
  const { start, end, preStart, preEnd, group } = useDates();

  // Data fetching
  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    //   group_by: "product_id",
    channel: channel ?? "amazon",
  };

  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["productDealStats", marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/coupons_reports/",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const filteredMetrics = useMemo(() => {
    if (!couponStats) return { results: [] };
    if (discountType === "all") return couponStats;

    return {
      ...couponStats,
      results: couponStats.results.filter((coupon) => coupon.discount_type === discountType),
    };
  }, [couponStats, discountType]);

  const metrics = useMemo(() => {
    if (!filteredMetrics) return {};

    let couponMetrics = { ...defaultMetrics };

    for (const coupon of filteredMetrics.results) {
      couponMetrics = incrementMetrics(couponMetrics, coupon);
    }

    return couponMetrics;
  }, [filteredMetrics]);

  const { couponTypeCount, discountAmountCount } = useMemo(() => {
    if (!couponStats) return {};

    const couponTypeCount = {
      all: couponStats?.results.length,
      AMOUNT_OFF_LIST_PRICE: 0,
      PERCENT_OFF_LIST_PRICE: 0,
    };

    const discountAmountCount = {
      AMOUNT_OFF_LIST_PRICE: {},
      PERCENT_OFF_LIST_PRICE: {},
    };

    for (const coupon of couponStats.results) {
      couponTypeCount[coupon.discount_type] += 1;

      if (!(coupon.discount_amount in discountAmountCount[coupon.discount_type])) {
        discountAmountCount[coupon.discount_type][coupon.discount_amount] = { ...defaultMetrics };
      }

      discountAmountCount[coupon.discount_type][coupon.discount_amount] = incrementMetrics(
        discountAmountCount[coupon.discount_type][coupon.discount_amount],
        coupon,
      );
    }

    return { couponTypeCount, discountAmountCount };
  }, [couponStats]);

  const buttunBuckets = [
    { label: "All Coupons", value: "all", counter: couponTypeCount?.all },
    { label: "Amount of List Price", value: "AMOUNT_OFF_LIST_PRICE", counter: couponTypeCount?.AMOUNT_OFF_LIST_PRICE },
    {
      label: "Percent of List Price",
      value: "PERCENT_OFF_LIST_PRICE",
      counter: couponTypeCount?.PERCENT_OFF_LIST_PRICE,
    },
  ];

  const breadCrumbItems = [
    {
      name: "Merchandising Dashboard",
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),

      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: "Dashboards",
    },
    {
      name: "Coupon Report",
      href: "/user/merchandising/coupons",
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col className="mb-4" xs={12}>
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>

      {isCouponStatsLoading && (
        <Row>
          <Col className="mb-4 px-3" xs={12}>
            <Loading fullPage={false} height={"300px"} />
          </Col>
        </Row>
      )}

      {!isCouponStatsLoading && (
        <Row>
          <Col className="mb-4 px-3" xs={12}>
            <Row className="p-3">
              <Col>
                <ButtonGroup
                  buttons={buttunBuckets}
                  value={discountType}
                  onSelect={(values) => {
                    setDiscountType(values);
                  }}
                />
              </Col>
            </Row>

            {metrics && (
              <Row
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "auto",
                  paddingTop: "1rem",
                  paddingBottom: "0.25rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Metric
                  title="Redemptions / Clips"
                  value={
                    <div>
                      {metrics?.redemptions} / {metrics?.clips}
                    </div>
                  }
                  previous={<div>Redemption Rate: {formatPercent(metrics?.redemption_rate, 1)}</div>}
                />
                <Metric
                  title="Budget Spent"
                  value={formatCurrency(metrics?.budget_spent, marketPlace)}
                  previous={<div>Budget: {formatCurrency(metrics?.budget, marketPlace)}</div>}
                />
                <Metric
                  title="Sales"
                  value={formatCurrency(metrics?.sales, marketPlace)}
                  previous={`Discounts ${formatCurrency(metrics?.total_discount, marketPlace)}`}
                />
                <Metric title="ACOS" value={formatPercent(metrics?.discount_percent_of_sales, 1)} />
                <Metric
                  title="Sales per Redemption"
                  value={formatCurrency(metrics?.sales_per_redemption, marketPlace)}
                />
                <Metric title="Cost per Redemption" value={formatCurrency(metrics?.cost_per_redemption, marketPlace)} />
              </Row>
            )}

            {discountType !== "all" && (
              <Accordion title={`Insights`} initialExpanded={true}>
                <InsightList
                  insights={discountAmountCount[discountType]}
                  discountType={discountType}
                  marketPlace={marketPlace}
                />
              </Accordion>
            )}

            <Accordion title={`Coupon List`} initialExpanded={true}>
              <CouponReport data={filteredMetrics} marketPlace={marketPlace} />
            </Accordion>
          </Col>
        </Row>
      )}
    </Container>
  );
};

const MerchCoupons = () => {
  const { coupon: couponId = null } = useParams();
  return (
    <DatesProvider>
      {couponId === null && <CouponDashboard />}
      {couponId !== null && <CouponDetails couponId={couponId} />}
    </DatesProvider>
  );
};

export default MerchCoupons;
